import { Builder } from '@builder.io/react';
import cn from 'classnames';
import capitalize from 'lodash-es/capitalize';
import { FC } from 'react';

import {
  ALLOW_BACKGROUND_COLORS,
  BackgroundClassMap,
  COLOR_NAME_PALE_GRAY,
} from '@/shared/constants/colors';
import {
  actionButtonInput,
  backgroundColorInput,
  componentLinkIdInput,
  contentMobileBehaviorInput,
  iconInput,
} from '@/shared/inputs';
import { getBuilderSpacingClass } from '@/shared/spacing';
import {
  BuilderString,
  ContentMobileBehavior,
  CtaVariant,
  SharedBuilderComponent,
} from '@/shared/types';

import { ActionVariant, BaseAction } from '../Action';
import { ComponentHeader } from '../ComponentHeader/ComponentHeader';
import { flatMediaInput } from '../Media';
import { Row } from './Row';
import { TopicRow } from './Topic';

interface BuilderInput extends SharedBuilderComponent {
  componentLinkId?: string;
  title: BuilderString;
  topics: TopicRow[];
  description: BuilderString;
  topicBackgroundColorClassName: string;

  fullWidth: boolean;

  // Header Button
  action: BaseAction;
  actionVariant?: ActionVariant;
  // List Button
  ctaVariant: CtaVariant;
  topicsAsBoxes: boolean;
  topicsWithTwoColumns?: boolean;
  topicsWithFourColumns?: boolean;
  topicsWithRichDescriptions?: boolean;
  topicsWithFullCardLinks?: boolean;
  contentMobileBehavior: ContentMobileBehavior;
}

type Props = BuilderInput & {
  locale: string;
};

const TOPIC_TONE_OPTIONS = [
  { label: 'Neutral', value: 'neutral' },
  { label: 'Success (green)', value: 'success' },
  { label: 'Warning (orange)', value: 'warning' },
  { label: 'Error (red)', value: 'error' },
];

export const TopicList: FC<Props> = ({
  componentLinkId,
  title = 'Title',
  description,

  fullWidth,

  // Header Button
  action,
  actionVariant,
  // List Button
  ctaVariant,

  topics = [],
  backgroundColorClassName,

  topicBackgroundColorClassName,
  topicsAsBoxes,
  topicsWithTwoColumns = false,
  topicsWithFourColumns = false,
  topicsWithRichDescriptions = false,
  topicsWithFullCardLinks = false,
  contentMobileBehavior = 'scroll',

  locale,
  builderBlock,
  builderState,
  attributes,
}) => {
  const spacingStyles = getBuilderSpacingClass(builderBlock, builderState);
  const blockLocale = locale || builderState?.context.locale || 'en';

  const headerSpacing = topicsAsBoxes
    ? 'u-m-b-xl u-m-b-lg-xxl-6'
    : 'u-m-b-xxl-6 u-m-b-lg-xxl-10';

  return (
    <section
      {...attributes}
      id={componentLinkId}
      className={cn(backgroundColorClassName, spacingStyles)}
    >
      <div className={cn('u-wrapper')}>
        <ComponentHeader
          title={title}
          description={description}
          fullWidth={fullWidth}
          action={action}
          actionVariant={actionVariant}
          locale={blockLocale}
          headerClassName={cn({
            [headerSpacing]: topics && topics.length > 0,
          })}
        >
          <Row
            contentMobileBehavior={contentMobileBehavior}
            ctaVariant={ctaVariant}
            topicsAsBoxes={topicsAsBoxes}
            topicsWithTwoColumns={topicsWithTwoColumns}
            topicsWithFourColumns={topicsWithFourColumns}
            topicsWithRichDescriptions={topicsWithRichDescriptions}
            topicsWithFullCardLinks={topicsWithFullCardLinks}
            topicBackgroundColorClassName={topicBackgroundColorClassName}
            topics={topics}
            locale={blockLocale}
          />
        </ComponentHeader>
      </div>
    </section>
  );
};

export function dataFetcher(locale: string) {
  return async () => ({
    locale,
  });
}

export const NAME = 'TopicList';

export function componentRegister() {
  Builder.registerComponent(TopicList, {
    name: NAME,
    inputs: [
      componentLinkIdInput(),
      {
        name: 'title',
        type: 'CatawikiEditor',
      },
      {
        name: 'description',
        type: 'CatawikiEditor',
      },
      {
        name: 'fullWidth',
        friendlyName: 'Show content in full width',
        helperText: 'Show content in full width or limit to 800px',
        type: 'boolean',
        defaultValue: true,
      },
      ...actionButtonInput({
        allowVariants: [ActionVariant.NONE, ActionVariant.TERTIARY],
      }),
      {
        name: 'ctaVariant',
        friendlyName: 'List Button Type',
        type: 'string',
        enum: ['none', 'tertiary', 'primary', 'secondary'],
        defaultValue: 'none',
      },
      { name: 'topicsAsBoxes', type: 'boolean', defaultValue: false },
      { name: 'topicsWithTwoColumns', type: 'boolean', defaultValue: false },
      { name: 'topicsWithFourColumns', type: 'boolean', defaultValue: false },
      {
        name: 'topicsWithRichDescriptions',
        friendlyName: 'Allow rich descriptions',
        type: 'boolean',
        defaultValue: false,
      },
      {
        name: 'topicsWithFullCardLinks',
        friendlyName: 'Link whole topic cards',
        type: 'boolean',
        defaultValue: false,
      },
      {
        name: 'topics',
        type: 'list',
        subFields: [
          { name: 'number', type: 'CatawikiEditor' },
          iconInput(),
          { name: 'title', type: 'CatawikiEditor' },
          {
            name: 'tone',
            friendlyName: 'Card tone',
            type: 'string',
            enum: TOPIC_TONE_OPTIONS,
            defaultValue: 'neutral',
          },
          { name: 'subtitle', type: 'CatawikiEditor' },
          {
            name: 'description',
            type: 'CatawikiEditor',
            required: true,
          },
          ...flatMediaInput({ useCatawikiPlainEditor: true }),
          {
            name: 'ctaText',
            friendlyName: 'Button Text',
            type: 'CatawikiPlainEditor',
          },
          {
            name: 'ctaUrl',
            friendlyName: 'Button URL',
            type: 'CatawikiPlainEditor',
          },
        ],
      },
      {
        name: 'topicBackgroundColorClassName',
        friendlyName: 'Topic Background Color',
        type: 'string',
        defaultValue: BackgroundClassMap[COLOR_NAME_PALE_GRAY],
        enum: ALLOW_BACKGROUND_COLORS.map((name) => ({
          label: capitalize(name),
          value: BackgroundClassMap[name],
        })),
      },
      contentMobileBehaviorInput(),
      backgroundColorInput(COLOR_NAME_PALE_GRAY),
    ],
    noWrap: true,
  });
  Builder.registerComponent(TopicList, {
    name: `v2:${NAME}`,
    inputs: [
      componentLinkIdInput(),
      {
        name: 'title',
        type: 'html',
      },
      {
        name: 'description',
        type: 'html',
      },
      {
        name: 'fullWidth',
        friendlyName: 'Show content in full width',
        helperText: 'Show content in full width or limit to 800px',
        type: 'boolean',
        defaultValue: true,
      },
      ...actionButtonInput({
        allowVariants: [ActionVariant.NONE, ActionVariant.TERTIARY],
        useCatawikiPlainEditor: false,
      }),
      {
        name: 'ctaVariant',
        friendlyName: 'List Button Type',
        type: 'string',
        enum: ['none', 'tertiary', 'primary', 'secondary'],
        defaultValue: 'none',
      },
      { name: 'topicsAsBoxes', type: 'boolean', defaultValue: false },
      { name: 'topicsWithTwoColumns', type: 'boolean', defaultValue: false },
      { name: 'topicsWithFourColumns', type: 'boolean', defaultValue: false },
      {
        name: 'topicsWithRichDescriptions',
        friendlyName: 'Allow rich descriptions',
        type: 'boolean',
        defaultValue: false,
      },
      {
        name: 'topicsWithFullCardLinks',
        friendlyName: 'Link whole topic cards',
        type: 'boolean',
        defaultValue: false,
      },
      {
        name: 'topics',
        type: 'list',
        subFields: [
          { name: 'number', type: 'html' },
          { name: 'title', type: 'html' },
          {
            name: 'tone',
            friendlyName: 'Card tone',
            type: 'string',
            enum: TOPIC_TONE_OPTIONS,
            defaultValue: 'neutral',
          },
          { name: 'subtitle', type: 'html' },
          {
            name: 'description',
            type: 'html',
            required: true,
          },
          ...flatMediaInput({ useCatawikiPlainEditor: false }),
          {
            name: 'ctaText',
            friendlyName: 'Button Text',
            type: 'CatawikiPlainEditorWithoutBuiltInTranslation',
          },
          {
            name: 'ctaUrl',
            friendlyName: 'Button URL',
            type: 'CatawikiPlainEditorWithoutBuiltInTranslation',
          },
        ],
      },
      {
        name: 'topicBackgroundColorClassName',
        friendlyName: 'Topic Background Color',
        type: 'string',
        defaultValue: BackgroundClassMap[COLOR_NAME_PALE_GRAY],
        enum: ALLOW_BACKGROUND_COLORS.map((name) => ({
          label: capitalize(name),
          value: BackgroundClassMap[name],
        })),
      },
      contentMobileBehaviorInput(),
      backgroundColorInput(COLOR_NAME_PALE_GRAY),
    ],
    noWrap: true,
  });
  return NAME;
}

export const componentDataFetchers = (locale: string) => ({
  [NAME]: dataFetcher(locale),
  [`v2:${NAME}`]: dataFetcher(locale),
});
